
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import AnkiImport from "./AnkiImport";
import QuizAikenImport from "./QuizAikenImport";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Loader2, Book, RotateCcw, Upload, ArrowLeft, Sparkles } from "lucide-react";

interface SubjectSelectorProps {
  subjects: string[];
  onSubjectSelect: (subject: string) => void;
  onReset: () => void;
}

const SubjectSelector: React.FC<SubjectSelectorProps> = ({
  subjects,
  onSubjectSelect,
  onReset,
}) => {
  const { session } = useAuthSession();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = React.useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  
  // Check admin status when session changes
  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!session?.user?.id) {
        setIsAdmin(false);
        return;
      }

      setIsCheckingAdmin(true);
      try {
        setIsAdmin(session.user.email === 'admin@admin.com');
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, [session]);

  if (!session) {
    return (
      <Card className="w-full p-8 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl text-center animate-fadeIn">
        <div className="p-4 bg-blue-50 rounded-lg text-blue-700 inline-flex items-center mb-4">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          <span>Authentication required</span>
        </div>
        <p className="text-gray-600">Please log in to access quiz subjects</p>
      </Card>
    );
  }

  if (isLoadingSubjects) {
    return (
      <Card className="w-full p-6 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl">
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="text-primary font-medium">Loading subjects...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full p-6 bg-gradient-to-b from-white to-blue-50 backdrop-blur-sm shadow-xl rounded-xl animate-fadeIn">
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4 border-b pb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 shadow-sm">
              <Book className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-primary">Select a Subject</h2>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onReset}
              size="sm"
              className="text-sm flex items-center gap-1 hover:bg-red-50 hover:text-red-600 hover:border-red-200 active:text-red-600 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </Button>
          </div>
        </div>
        
        {subjects.length === 0 ? (
          <div className="text-center py-12 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <Book className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 text-lg font-medium">No subjects available in this category</p>
            <p className="text-gray-500 mt-1 text-sm">Try selecting a different category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {subjects.map((subject) => (
              <div key={subject} className="flex flex-col gap-2">
                <button
                  className="w-full h-auto py-4 px-5 text-left flex justify-between items-center rounded-lg transition-all duration-300
                    border border-gray-200 hover:border-primary/30 text-gray-700 hover:text-gray-800
                    bg-gradient-to-br from-white to-blue-50 hover:from-blue-50 hover:to-indigo-100 
                    shadow-sm hover:shadow active:text-gray-800 active:bg-blue-100 hover-lift"
                  onClick={() => onSubjectSelect(subject)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-md bg-primary/10">
                      <Sparkles className="h-4 w-4 text-primary" />
                    </div>
                    <span className="font-medium">{subject}</span>
                  </div>
                  <ArrowLeft className="h-4 w-4 rotate-180 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-transform" />
                </button>
                {isAdmin && !isCheckingAdmin && (
                  <div className="px-2 space-y-2">
                    <AnkiImport 
                      subject={subject}
                      onImport={() => {
                        toast.success(`Anki questions imported for ${subject}`);
                      }}
                    />
                    <QuizAikenImport
                      subject={subject}
                      onImport={() => {
                        toast.success(`Aiken questions imported for ${subject}`);
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg text-sm text-purple-700 animate-fadeIn hover-lift">
            <p className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              <span>Select a subject to start a quiz and test your knowledge!</span>
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SubjectSelector;
